import React, { useState, useEffect } from 'react';
import '../../styles/mission-shakti/main.css';
import { smtpApis } from '../../Auth/Api'; // adjust this path to match where your apis.js file actually lives

const RECAPTCHA_SITE_KEY = "6LcXI1MtAAAAAKyOx9WKnsHL0pjQ8QVftCBPtr4B";

const PLEDGE_STORAGE_KEY = 'ccsu-mission-shakti-pledged';

const Main = () => {
    const [modalStep, setModalStep] = useState(null);
    const [formData, setFormData] = useState({ fullName: '', phone: '', email: '' });
    const [hasPledged, setHasPledged] = useState(() => localStorage.getItem(PLEDGE_STORAGE_KEY) === 'true');
    const [pledgeCount, setPledgeCount] = useState(null); // null = still loading from server
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Load reCAPTCHA v3 script once, on mount.
    useEffect(() => {
        if (document.querySelector('script[src*="recaptcha/api.js"]')) return;

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        document.body.appendChild(script);
    }, []);

    // Fetch the real total pledge count from the backend on mount.
    useEffect(() => {
        let cancelled = false;

        const loadCount = async () => {
            try {
                const response = await fetch(`${smtpApis.baseUrl}${smtpApis.getMissionShaktiPledgeCount}`);
                if (!response.ok) throw new Error('Failed to load pledge count');
                const data = await response.json();
                if (!cancelled) setPledgeCount(data.count ?? 0);
            } catch {
                // Fall back gracefully if the count endpoint isn't reachable yet.
                if (!cancelled) setPledgeCount(0);
            }
        };

        loadCount();
        return () => { cancelled = true; };
    }, []);

    const closeModal = () => {
        setModalStep(null);
        setFormData({ fullName: '', phone: '', email: '' });
        setSubmitError('');
    };

    const updateForm = (event) => {
        const { name, value } = event.target;
        setFormData((current) => ({ ...current, [name]: value }));
    };

    const submitDetails = (event) => {
        event.preventDefault();
        setModalStep('pledge');
    };

    // Runs the invisible reCAPTCHA v3 check and resolves with a token.
    const getCaptchaToken = () => {
        return new Promise((resolve, reject) => {
            if (!window.grecaptcha || !window.grecaptcha.execute) {
                reject(new Error('reCAPTCHA has not loaded yet'));
                return;
            }
            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute(RECAPTCHA_SITE_KEY, { action: 'mission_shakti_pledge' })
                    .then(resolve)
                    .catch(reject);
            });
        });
    };

    const takePledge = async () => {
        setSubmitError('');
        setSubmitting(true);

        try {
            const captchaToken = await getCaptchaToken();

            const response = await fetch(`${smtpApis.baseUrl}${smtpApis.submitMissionShaktiPledge}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fullName: formData.fullName,
                    phone: formData.phone,
                    email: formData.email,
                    captchaToken,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                    // Use the backend's actual message (e.g. "This email has already taken the pledge.")
                    throw new Error(data.message || `Server responded with status ${response.status}`);
                }

            if (!hasPledged) {
                localStorage.setItem(PLEDGE_STORAGE_KEY, 'true');
                setHasPledged(true);
            }

            // Prefer the server's authoritative count; fall back to a local +1.
            setPledgeCount((current) =>
                typeof data.count === 'number' ? data.count : (current ?? 0) + 1
            );

            setModalStep('thankYou');
        }catch (err) {
                setSubmitError(
                    err?.message === 'reCAPTCHA has not loaded yet'
                        ? 'Could not verify reCAPTCHA. Please refresh the page and try again.'
                        : err?.message || 'Something went wrong while submitting your pledge. Please try again.'
                );
            } finally {
            setSubmitting(false);
        }
    };

    const displayCount = pledgeCount ?? 0;

    return (
        <div id="MissionShaktiMain">
            <div className="mission-shakti-main">
                <div className="container main-box pb-5">
                    <div className="box">
                        <div className="mission-shakti-heading">
                            <h1 className="my-heading mt-3">Mission Shakti</h1>
                            <div className="pledge-counter" aria-label={`${displayCount} people have taken the pledge`}>
                                <span className="pledge-counter-icon" aria-hidden="true">&#128101;</span>
                                <span>
                                    {pledgeCount === null ? (
                                        'Loading pledges…'
                                    ) : (
                                        <><strong>{displayCount.toLocaleString('en-IN')}</strong> pledged</>
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="details" id="founder">
                            <span>
                                Mission Shakti is an important initiative of the Government of Uttar Pradesh aimed at ensuring the safety, dignity, and empowerment of women and girls. It focuses on creating a secure environment through awareness campaigns, self-defence training, legal support, and quick response mechanisms to prevent crimes against women.
                                <br /><br />
                                Under this mission, educational institutions such as Chaudhary Charan Singh University play a vital role in spreading awareness among students. Programs like seminars, workshops, helplines, and counselling sessions are organized to educate young women about their rights, safety measures, and available government support systems.
                                <br /><br />
                                Overall, Mission Shakti promotes women&apos;s empowerment by encouraging confidence, independence, and active participation of women in society, thereby contributing to social development and gender equality.
                            </span>
                        </div>
                        <div className="mission-pledge-callout">
                            <div>
                                <p className="mission-pledge-eyebrow">Stand with Mahila Adhyayan Kendra</p>
                                <h2>I PLEDGE FOR A HEALTHY AND THRIVING BHARAT.</h2>
                            </div>
                            <button type="button" className="mission-pledge-button" onClick={() => setModalStep('details')}>
                                Take the pledge <span aria-hidden="true">&#8594;</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {modalStep && (
                <div className="pledge-modal-backdrop" role="presentation" onMouseDown={closeModal}>
                    <section className="pledge-modal" role="dialog" aria-modal="true" aria-labelledby="pledge-modal-title" onMouseDown={(event) => event.stopPropagation()}>
                        <button type="button" className="pledge-modal-close" onClick={closeModal} aria-label="Close pledge form">&times;</button>

                        {modalStep === 'details' && (
                            <form onSubmit={submitDetails}>
                                <p className="pledge-step">Step 1 of 3</p>
                                <h2 id="pledge-modal-title">User Form Details</h2>
                                <p className="pledge-modal-intro">I PLEDGE FOR A HEALTHY AND THRIVING BHARAT.</p>
                                <label htmlFor="fullName">Full name <span>*</span></label>
                                <input id="fullName" name="fullName" value={formData.fullName} onChange={updateForm} autoComplete="name" required />
                                <div className="pledge-form-row">
                                    <div>
                                        <label htmlFor="phone">Phone number <span>*</span></label>
                                        <input id="phone" name="phone" type="tel" value={formData.phone} onChange={updateForm} autoComplete="tel" pattern="[0-9+ -]{10,15}" title="Enter a valid phone number" required />
                                    </div>
                                    <div>
                                        <label htmlFor="email">Email address <span>*</span></label>
                                        <input id="email" name="email" type="email" value={formData.email} onChange={updateForm} autoComplete="email" required />
                                    </div>
                                </div>
                                <button className="mission-pledge-button modal-action" type="submit">Continue <span aria-hidden="true">&#8594;</span></button>
                            </form>
                        )}

                        {modalStep === 'pledge' && (
                            <div>
                                <p className="pledge-step">Step 2 of 3</p>
                                <h2 id="pledge-modal-title">I PLEDGE FOR A HEALTHY AND THRIVING BHARAT.</h2>
                                <p className="pledge-modal-intro">I, <strong>{formData.fullName}</strong>, pledge to:</p>
                                <ul className="pledge-list">
                                    <li>By taking care of my health through a disciplined and sustainable lifestyle, eating mindfully and workout at least 3 times a week.</li>
                                    <li>By practicing meditation daily to take care of my mental well-being and encourage others to do the same.</li>
                                    <li>By eating and promoting natural, organic and healthy diet and the benefits of exercising to my friends, families and associations.</li>
                                </ul>

                                {submitError && (
                                    <div className="text-danger text-center mb-3" style={{ fontSize: '0.9em' }}>
                                        {submitError}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="mission-pledge-button modal-action"
                                    onClick={takePledge}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Submitting…' : (
                                        <>I take this pledge <span aria-hidden="true">&#10003;</span></>
                                    )}
                                </button>

                                <p className="text-muted mt-2" style={{ fontSize: '0.75em' }}>
                                    This site is protected by reCAPTCHA and the Google{' '}
                                    <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{' '}
                                    <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a> apply.
                                </p>
                            </div>
                        )}

                        {modalStep === 'thankYou' && (
                            <div className="pledge-thank-you">
                                <div className="pledge-success-icon" aria-hidden="true">&#10003;</div>
                                <p className="pledge-step">Step 3 of 3</p>
                                <h2 id="pledge-modal-title">I PLEDGE FOR A HEALTHY AND THRIVING BHARAT.</h2>
                                <h3 id="pledge-modal-title">Thank you for being a part of our pledge</h3>
                                <p>Each pledge counts. Each effort counts.</p>
                                <div className="thank-you-count"><strong>{displayCount.toLocaleString('en-IN')}</strong><span>people have pledged</span></div>
                                <button type="button" className="mission-pledge-button modal-action" onClick={closeModal}>Done</button>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default Main;